import { computed } from 'mobx'
import { task } from 'mobx-task'
import type { GetCurrentMemberFn } from '@/features/iam'
import { type Member } from '@/features/iam/members/Member'
import { Room } from '@/features/messaging/rooms/Room'
import { type RoomStore } from '@/features/messaging/rooms/RoomStore'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type { GetExternalUrlDelegate } from '@/features/shell/platform/types'
import type { Id } from '@/infra/id'

/**
 * View model for a consultation room.
 */
export class RoomViewModel {
  constructor(
    private readonly _getRoomId: () => Id,
    private readonly _roomStore: RoomStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getExternalUrlDelegate: GetExternalUrlDelegate,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Whether the room is closed.
   */
  @computed
  get isRoomClosed() {
    return this.room.status === 'Closed'
  }

  /**
   * The members connected to the room.
   */
  @computed
  get participants(): Member[] {
    return this.room.activeParticipants
  }

  /**
   * Whether the room is active.
   */
  @computed
  get activeRoom(): boolean {
    return this.room.status === 'Active'
  }

  /**
   * The room.
   */
  @computed
  get room() {
    return this._roomStore.rooms.get(this._getRoomId()) ?? Room.none
  }

  /**
   * Opens the room in a new window.
   */
  join = task.resolved(async () => {
    const handle = this._getExternalUrlDelegate().createHandle()

    try {
      const roomDescriptor = await this._roomStore
        .joinRoom(this.room.id)
        .catch(this._createAlertErrorHandler('Joining the call failed'))

      if (roomDescriptor.type === 'Whereby') {
        const roomUrl = new URL(roomDescriptor.roomUrl)
        const displayName = this._getCurrentMember().displayName

        handle.open(`${roomUrl}&displayName=${displayName}&logo=off`)
        return
      }

      handle.abort()
    } catch (err) {
      handle.abort()
      throw err
    }
  })
}
